export type FigmaTemplateLayoutMode =
  | "absolute-layout"
  | "constraint-layout"
  | "responsive-layout";

export type FigmaTemplateMatchDecision =
  | "matchClosedCore"
  | "modifyExisting"
  | "composeFromLowerLayer"
  | "createOpenFrontierCandidate";

export interface FigmaOntologyPaint {
  type?: string;
  visible?: boolean;
  opacity?: number;
  imageRef?: string;
  color?: { r?: number; g?: number; b?: number; a?: number };
  gradientStops?: Array<{
    color?: { r?: number; g?: number; b?: number; a?: number };
    position?: number;
  }>;
}

export interface FigmaOntologyNode {
  id?: string;
  name?: string;
  type?: string;
  characters?: string;
  fills?: FigmaOntologyPaint[];
  strokes?: FigmaOntologyPaint[];
  cornerRadius?: number;
  absoluteBoundingBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  effects?: Array<{
    type?: string;
    visible?: boolean;
    radius?: number;
    offset?: { x?: number; y?: number };
    color?: FigmaOntologyPaint["color"];
  }>;
  style?: {
    fontFamily?: string;
    fontPostScriptName?: string;
    fontWeight?: number;
    fontSize?: number;
    lineHeightPx?: number;
    letterSpacing?: number;
  };
  fillGeometry?: Array<{ path?: string; windingRule?: string }>;
  strokeGeometry?: Array<{ path?: string; windingRule?: string }>;
  children?: FigmaOntologyNode[];
}

export interface FigmaDesignOntology {
  schemaVersion: "cogo.figma-design-ontology.v1";
  source: {
    figmaFileId: string;
    figmaNodeId: string;
    rootName?: string;
    rootType?: string;
  };
  authority: {
    layout: "figma.absoluteBoundingBox";
    style: "figma.fills/strokes/style/effects";
    assets: "figma.imageRef/rendered-assets";
    vectors: "figma.geometry.paths|figma.svg-render|cogo.primitive-fallback";
    llmMayOverride: false;
  };
  pages: Array<{
    id: string;
    name: string;
    routeHint: string;
    box: { x: number; y: number; width: number; height: number };
    nodeCount: number;
    imageFillNodeCount: number;
    vectorLikeNodeCount: number;
    vectorAreaRatio: number;
  }>;
  tokens: {
    counts: FigmaTemplateCompilerFacts["exactTokenCounts"];
  };
  assets: {
    imageFillNodeIds: string[];
    localImageAssetNodeIds: string[];
    imageCoverage: number;
  };
  vectors: {
    vectorLikeNodeIds: string[];
    geometryNodeIds: string[];
    localSvgNodeIds: string[];
    primitiveFallbackNodeIds: string[];
    vectorCoverage: number;
    vectorAreaRatio: number;
    fallbackStrategy:
      | "exact-vector"
      | "primitive-fallback"
      | "source-raster-required";
    sourceUnavailableReason?: string;
  };
  quality: {
    status: "pass" | "warn" | "fail";
    reasons: string[];
  };
}

export interface FigmaTemplateLayoutSignature {
  viewportClass: "mobile" | "tablet" | "desktop" | "unknown";
  topology:
    | "single-page"
    | "bounded-page-set"
    | "curated-page-subset"
    | "raw-node"
    | "unknown";
  primaryFlow: "top-to-bottom" | "left-to-right" | "canvas-grid" | "unknown";
  density:
    | "sparse"
    | "standard"
    | "content-heavy"
    | "component-heavy"
    | "unknown";
  regions: string[];
  componentKinds: string[];
  interactionAffordances: string[];
  pageCount: number;
  frameSizeBucket: string;
}

export interface FigmaTemplateCompilerFacts {
  sourceNodeIds: string[];
  pageBoxes: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  exactTokenCounts: {
    color: number;
    typography: number;
    radius: number;
    shadow: number;
    gradient: number;
  };
  assetFacts: {
    imageFillNodeIds: string[];
    gradientFillNodeIds: string[];
  };
}

export interface FigmaTemplateSearchRepresentation {
  schemaVersion: "cogo.figma-template-search-representation.v1";
  key: string;
  source: {
    figmaFileId: string;
    figmaNodeId: string;
    rootName?: string;
    rootType?: string;
  };
  searchText: string;
  aliases: string[];
  semanticLabels: string[];
  layoutSignature: FigmaTemplateLayoutSignature;
  componentAnatomy: Array<{
    kind: string;
    count: number;
    sampleNames: string[];
  }>;
  designTokenSummary: {
    colorCount: number;
    typographyCount: number;
    radiusCount: number;
    shadowCount: number;
    gradientCount: number;
  };
  compilerFacts: FigmaTemplateCompilerFacts;
}

export interface FigmaTemplateMatchQuery {
  userIntent?: string;
  expectedViewportClass?: FigmaTemplateLayoutSignature["viewportClass"];
  expectedRegions?: string[];
  expectedComponentKinds?: string[];
  expectedInteractionAffordances?: string[];
  scope?: "project" | "template" | "designSystem" | "globalLibrary";
}

export interface FigmaTemplateMatchScore {
  schemaVersion: "cogo.figma-template-match-score.v1";
  finalScore: number;
  decision: FigmaTemplateMatchDecision;
  components: {
    semanticIntentScore: number;
    anatomySlotScore: number;
    layoutTopologyScore: number;
    domainEntityScore: number;
    interactionAffordanceScore: number;
    designTokenCompatibility: number;
    provenanceScopeScore: number;
  };
  weights: {
    semanticKindName: number;
    anatomySlot: number;
    designTokenFit: number;
    layoutTopology: number;
    interactionAffordance: number;
    provenanceConfidence: number;
  };
  thresholds: {
    matchClosedCore: number;
    modifyExisting: number;
    composeFromLowerLayer: number;
  };
  notes: string[];
}

export interface CogoLayoutGenerationBoundary {
  schemaVersion: "cogo.layout-generation-boundary.v1";
  mode: FigmaTemplateLayoutMode;
  authority:
    | "figma.absoluteBoundingBox"
    | "ontology.layoutRecipe"
    | "ontology.responsiveLayoutRecipe";
  engineRequired: boolean;
  llmMayEmitRawCoordinates: boolean;
  requiredInputs: string[];
  reason: string;
}

export type FigmaTemplateGenerationPath =
  | "reuse-template-closed-core"
  | "modify-template-with-locked-layout"
  | "compose-from-lower-layer-ontology"
  | "create-frontier-candidate-for-review";

export interface FigmaTemplateEnforcementContract {
  schemaVersion: "cogo.figma-template-enforcement-contract.v1";
  selectedTemplate: {
    key: string;
    figmaFileId: string;
    figmaNodeId: string;
    rootName?: string;
    rootType?: string;
    scope: NonNullable<FigmaTemplateMatchQuery["scope"]>;
    matchDecision: FigmaTemplateMatchDecision;
    matchScore: number;
  };
  authority: {
    selection: "tool.match-score";
    layout: CogoLayoutGenerationBoundary["authority"];
    style: "figma.fills/strokes/style/effects";
    tokens: "figma.fills/strokes/style/effects";
    assets: "figma.imageRef/rendered-assets";
    actionflow: "separate-actionflow-contract";
    llmMayChooseTemplate: false;
    llmMayOverrideLayout: false;
    llmMayOverrideStyle: false;
    llmMayPromoteFallback: false;
  };
  generationPath: FigmaTemplateGenerationPath;
  requiredCompilerInputs: string[];
  lockedFacts: FigmaTemplateCompilerFacts;
  fallbackPolicy: {
    pageMissing: "compose-page-from-ui-block-ontology";
    uiBlockMissing: "compose-ui-block-from-component-ontology";
    componentMissing: "create-component-candidate-and-queue-review";
    modificationDecision:
      | "reuse-as-is"
      | "tool-diff-against-locked-facts"
      | "lower-layer-composition"
      | "frontier-review";
    promotionRequiresReview: true;
    validationRequired: true;
  };
  validationGates: string[];
  notes: string[];
}

const STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "app",
  "button",
  "design",
  "frame",
  "group",
  "icon",
  "image",
  "of",
  "page",
  "rectangle",
  "screen",
  "the",
  "vector",
]);

function collectNodes(node: FigmaOntologyNode): FigmaOntologyNode[] {
  return [node, ...(node.children ?? []).flatMap(collectNodes)];
}

function normalizeTerm(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/[#()[\]{}.,:;!?/\\|_+="'`~@$%^&*<>-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isVectorSearchNoiseWord(word: string): boolean {
  return (
    STOPWORDS.has(word) ||
    /^\d+(?:\.\d+)?(?:px|pt|dp|sp|rem|em|%)?$/.test(word) ||
    /^(?:w|h|x|y)\d+$/.test(word) ||
    /^rgba?$/.test(word) ||
    /^#[0-9a-f]{3,8}$/i.test(word)
  );
}

function words(value: string): string[] {
  return normalizeTerm(value)
    .split(" ")
    .map((word) => word.trim())
    .filter((word) => word.length > 1 && !isVectorSearchNoiseWord(word));
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function numericBox(
  node: FigmaOntologyNode,
): { x: number; y: number; width: number; height: number } | null {
  const x = Number(node.absoluteBoundingBox?.x);
  const y = Number(node.absoluteBoundingBox?.y);
  const width = Number(node.absoluteBoundingBox?.width);
  const height = Number(node.absoluteBoundingBox?.height);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

function routeHintForPage(index: number, name: string | undefined): string {
  if (index === 0) return "/";
  return `/${normalizeTerm(name ?? `page-${index + 1}`).replace(/\s+/g, "-")}`;
}

function hasImageFill(node: FigmaOntologyNode): boolean {
  return (node.fills ?? []).some(
    (paint) => paint.visible !== false && paint.type === "IMAGE",
  );
}

function isVectorLikeNode(node: FigmaOntologyNode): boolean {
  return ["VECTOR", "LINE", "BOOLEAN_OPERATION", "POLYGON", "STAR"].includes(
    node.type ?? "",
  );
}

function hasVectorGeometry(node: FigmaOntologyNode): boolean {
  return [...(node.fillGeometry ?? []), ...(node.strokeGeometry ?? [])].some(
    (geometry) =>
      typeof geometry.path === "string" && geometry.path.trim().length > 0,
  );
}

function isInsidePage(
  node: FigmaOntologyNode,
  page: FigmaOntologyNode,
): boolean {
  const nodeBox = numericBox(node);
  const pageBox = numericBox(page);
  if (!nodeBox || !pageBox) return false;
  return (
    nodeBox.x >= pageBox.x &&
    nodeBox.y >= pageBox.y &&
    nodeBox.x + nodeBox.width <= pageBox.x + pageBox.width &&
    nodeBox.y + nodeBox.height <= pageBox.y + pageBox.height
  );
}

function areaRatioForNodes(
  nodes: FigmaOntologyNode[],
  pages: FigmaOntologyNode[],
): number {
  const pageArea = pages
    .map(numericBox)
    .filter((box): box is NonNullable<typeof box> => Boolean(box))
    .reduce((sum, box) => sum + box.width * box.height, 0);
  if (pageArea <= 0) return 0;
  const nodeArea = nodes
    .map(numericBox)
    .filter((box): box is NonNullable<typeof box> => Boolean(box))
    .reduce((sum, box) => sum + box.width * box.height, 0);
  return Number((nodeArea / pageArea).toFixed(4));
}

function coverageRatio(total: number, covered: number): number {
  if (total <= 0) return 1;
  return Number((Math.min(total, covered) / total).toFixed(3));
}

function viewportClassForPages(
  pages: FigmaOntologyNode[],
): FigmaTemplateLayoutSignature["viewportClass"] {
  const firstBox = pages.map(numericBox).find(Boolean);
  if (!firstBox) return "unknown";
  if (firstBox.width <= 480) return "mobile";
  if (firstBox.width <= 900) return "tablet";
  return "desktop";
}

function frameSizeBucketForPages(pages: FigmaOntologyNode[]): string {
  const firstBox = pages.map(numericBox).find(Boolean);
  if (!firstBox) return "unknown";
  const widthBucket =
    firstBox.width <= 480
      ? "mobile"
      : firstBox.width <= 900
        ? "tablet"
        : "desktop";
  const heightBucket =
    firstBox.height <= 700
      ? "short"
      : firstBox.height <= 950
        ? "standard"
        : "tall";
  return `${widthBucket}-${heightBucket}`;
}

function componentKindForNode(node: FigmaOntologyNode): string | null {
  const haystack = `${node.name ?? ""} ${node.characters ?? ""} ${
    node.type ?? ""
  }`.toLowerCase();
  if (/\b(button|cta|submit|continue|checkout|exchange)\b/.test(haystack))
    return "button";
  if (
    /\b(input|textfield|text field|search|email|password|amount)\b/.test(
      haystack,
    )
  )
    return "input";
  if (/\b(card|tile|product|account)\b/.test(haystack)) return "card";
  if (/\b(tab|tabs|category|filter)\b/.test(haystack)) return "tabs";
  if (/\b(table|grid)\b/.test(haystack)) return "table";
  if (/\b(nav|navbar|menu|bottom bar|tab bar)\b/.test(haystack)) return "nav";
  if (/\bmodal|dialog\b/.test(haystack)) return "modal";
  if (/\b(chart|graph|report|analytics|balance)\b/.test(haystack))
    return "data-display";
  if (/\bimage|photo|hero|avatar|ellipse\b/.test(haystack)) return "image";
  return null;
}

function regionForNode(node: FigmaOntologyNode): string | null {
  const haystack = `${node.name ?? ""} ${node.characters ?? ""}`.toLowerCase();
  const box = numericBox(node);
  if (/\b(status bar|status)\b/.test(haystack)) return "status-bar";
  if (/\b(header|top bar|title)\b/.test(haystack)) return "header";
  if (/\b(hero|banner|discover|choose)\b/.test(haystack)) return "hero";
  if (/\b(category|filter|tabs)\b/.test(haystack)) return "filter-tabs";
  if (/\b(card|product|item|account)\b/.test(haystack)) return "card-list";
  if (/\b(form|input|amount|password|search)\b/.test(haystack)) return "form";
  if (/\b(bottom nav|tab bar|navbar|navigation)\b/.test(haystack))
    return "bottom-nav";
  if (box && box.y < 120) return "header";
  if (box && box.height > 180 && box.y < 360) return "hero";
  return null;
}

function interactionForNode(node: FigmaOntologyNode): string | null {
  const haystack = `${node.name ?? ""} ${node.characters ?? ""}`.toLowerCase();
  if (/\b(add|cart|bag|checkout)\b/.test(haystack)) return "cart";
  if (/\b(exchange|send|transfer|withdraw|recharge)\b/.test(haystack))
    return "submit";
  if (/\b(search|filter)\b/.test(haystack)) return "search";
  if (/\b(back|next|continue|detail|open)\b/.test(haystack)) return "navigate";
  if (/\b(login|sign in|password)\b/.test(haystack)) return "auth";
  return null;
}

function tokenCounts(
  nodes: FigmaOntologyNode[],
): FigmaTemplateCompilerFacts["exactTokenCounts"] {
  const colors = new Set<string>();
  const typography = new Set<string>();
  const radii = new Set<string>();
  const shadows = new Set<string>();
  let gradient = 0;

  for (const node of nodes) {
    for (const paint of [...(node.fills ?? []), ...(node.strokes ?? [])]) {
      if (paint.visible === false) continue;
      if (paint.type === "SOLID" && paint.color) {
        colors.add(
          [
            Math.round((paint.color.r ?? 0) * 255),
            Math.round((paint.color.g ?? 0) * 255),
            Math.round((paint.color.b ?? 0) * 255),
            paint.color.a ?? 1,
          ].join(","),
        );
      }
      if (paint.type?.startsWith("GRADIENT")) gradient += 1;
    }
    if (node.style?.fontFamily) typography.add(`font:${node.style.fontFamily}`);
    if (typeof node.style?.fontSize === "number")
      typography.add(`size:${Math.round(node.style.fontSize)}`);
    if (typeof node.cornerRadius === "number")
      radii.add(String(Math.round(node.cornerRadius)));
    for (const effect of node.effects ?? []) {
      if (
        effect.visible !== false &&
        (effect.type === "DROP_SHADOW" || effect.type === "INNER_SHADOW")
      ) {
        shadows.add(
          `${effect.type}:${effect.offset?.x ?? 0}:${effect.offset?.y ?? 0}:${
            effect.radius ?? 0
          }`,
        );
      }
    }
  }

  return {
    color: colors.size,
    typography: typography.size,
    radius: radii.size,
    shadow: shadows.size,
    gradient,
  };
}

function densityFor(
  nodes: FigmaOntologyNode[],
  pages: FigmaOntologyNode[],
): FigmaTemplateLayoutSignature["density"] {
  const pageCount = Math.max(1, pages.length);
  const nodesPerPage = nodes.length / pageCount;
  if (nodesPerPage < 35) return "sparse";
  if (nodesPerPage < 120) return "standard";
  if (nodesPerPage < 280) return "content-heavy";
  return "component-heavy";
}

function primaryFlowFor(
  pages: FigmaOntologyNode[],
): FigmaTemplateLayoutSignature["primaryFlow"] {
  if (pages.length <= 1) return "top-to-bottom";
  const boxes = pages
    .map(numericBox)
    .filter((box): box is NonNullable<typeof box> => Boolean(box));
  if (boxes.length < 2) return "unknown";
  const horizontalSpread =
    Math.max(...boxes.map((box) => box.x)) -
    Math.min(...boxes.map((box) => box.x));
  const verticalSpread =
    Math.max(...boxes.map((box) => box.y)) -
    Math.min(...boxes.map((box) => box.y));
  if (horizontalSpread > verticalSpread * 1.5) return "left-to-right";
  if (verticalSpread > horizontalSpread * 1.5) return "top-to-bottom";
  return "canvas-grid";
}

function scoreSetOverlap(
  expected: string[] | undefined,
  actual: string[],
): number {
  if (!expected || expected.length === 0) return actual.length > 0 ? 0.8 : 0.3;
  const actualSet = new Set(actual);
  const matches = expected.filter((value) => actualSet.has(value)).length;
  return Number((matches / expected.length).toFixed(3));
}

function scoreTextOverlap(
  intent: string | undefined,
  searchText: string,
): number {
  if (!intent?.trim()) return searchText.trim() ? 0.75 : 0.2;
  const intentWords = words(intent);
  if (intentWords.length === 0) return 0.2;
  const searchWords = new Set(words(searchText));
  const matches = intentWords.filter((word) => searchWords.has(word)).length;
  return Number((matches / intentWords.length).toFixed(3));
}

function decisionForScore(score: number): FigmaTemplateMatchDecision {
  if (score >= 0.85) return "matchClosedCore";
  if (score >= 0.7) return "modifyExisting";
  if (score >= 0.5) return "composeFromLowerLayer";
  return "createOpenFrontierCandidate";
}

function generationPathForDecision(
  decision: FigmaTemplateMatchDecision,
): FigmaTemplateGenerationPath {
  switch (decision) {
    case "matchClosedCore":
      return "reuse-template-closed-core";
    case "modifyExisting":
      return "modify-template-with-locked-layout";
    case "composeFromLowerLayer":
      return "compose-from-lower-layer-ontology";
    case "createOpenFrontierCandidate":
      return "create-frontier-candidate-for-review";
  }
}

function modificationDecisionFor(
  decision: FigmaTemplateMatchDecision,
): FigmaTemplateEnforcementContract["fallbackPolicy"]["modificationDecision"] {
  switch (decision) {
    case "matchClosedCore":
      return "reuse-as-is";
    case "modifyExisting":
      return "tool-diff-against-locked-facts";
    case "composeFromLowerLayer":
      return "lower-layer-composition";
    case "createOpenFrontierCandidate":
      return "frontier-review";
  }
}

function joinSearchText(parts: string[]): string {
  return unique(parts.flatMap(words)).join(" ");
}

export function buildFigmaTemplateSearchRepresentation(params: {
  figmaFileId: string;
  figmaNodeId: string;
  root: FigmaOntologyNode;
  pages: FigmaOntologyNode[];
  pageSelectionMode: FigmaTemplateLayoutSignature["topology"];
}): FigmaTemplateSearchRepresentation {
  const allNodes = collectNodes(params.root);
  const importedNodes = params.pages.flatMap(collectNodes);
  const semanticLabels = unique(
    importedNodes
      .flatMap((node) => [node.name, node.characters])
      .filter((value): value is string => Boolean(value?.trim()))
      .map(normalizeTerm)
      .filter(Boolean),
  ).slice(0, 80);
  const componentKinds = importedNodes
    .map(componentKindForNode)
    .filter((kind): kind is string => Boolean(kind));
  const componentAnatomy = Object.entries(
    componentKinds.reduce<Record<string, { count: number; names: string[] }>>(
      (acc, kind, index) => {
        const node = importedNodes[index];
        acc[kind] ??= { count: 0, names: [] };
        acc[kind].count += 1;
        if (node?.name && acc[kind].names.length < 6)
          acc[kind].names.push(node.name);
        return acc;
      },
      {},
    ),
  ).map(([kind, value]) => ({
    kind,
    count: value.count,
    sampleNames: unique(value.names),
  }));
  const inferredRegionsFromComponents = [
    componentKinds.includes("card") ? "card-list" : null,
    componentKinds.includes("input") ? "form" : null,
    componentKinds.includes("nav") ? "bottom-nav" : null,
  ].filter((region): region is string => Boolean(region));
  const regions = unique([
    ...importedNodes
      .map(regionForNode)
      .filter((region): region is string => Boolean(region)),
    ...inferredRegionsFromComponents,
  ]);
  const interactionAffordances = unique(
    importedNodes
      .map(interactionForNode)
      .filter((interaction): interaction is string => Boolean(interaction)),
  );
  const exactTokenCounts = tokenCounts(allNodes);
  const pageBoxes = params.pages
    .map((page) => {
      const box = numericBox(page);
      return box
        ? {
            id: page.id ?? "unknown",
            name: page.name ?? "Page",
            ...box,
          }
        : null;
    })
    .filter((value): value is FigmaTemplateCompilerFacts["pageBoxes"][number] =>
      Boolean(value),
    );
  const imageFillNodeIds = importedNodes
    .filter((node) =>
      (node.fills ?? []).some(
        (paint) => paint.visible !== false && paint.type === "IMAGE",
      ),
    )
    .map((node) => node.id)
    .filter((id): id is string => Boolean(id));
  const gradientFillNodeIds = importedNodes
    .filter((node) =>
      (node.fills ?? []).some(
        (paint) =>
          paint.visible !== false && paint.type?.startsWith("GRADIENT"),
      ),
    )
    .map((node) => node.id)
    .filter((id): id is string => Boolean(id));
  const layoutSignature: FigmaTemplateLayoutSignature = {
    viewportClass: viewportClassForPages(params.pages),
    topology: params.pageSelectionMode,
    primaryFlow: primaryFlowFor(params.pages),
    density: densityFor(importedNodes, params.pages),
    regions,
    componentKinds: unique(componentKinds),
    interactionAffordances,
    pageCount: params.pages.length,
    frameSizeBucket: frameSizeBucketForPages(params.pages),
  };
  const aliases = unique([
    normalizeTerm(params.root.name ?? ""),
    ...params.pages.map((page) => normalizeTerm(page.name ?? "")),
    ...componentAnatomy.map((component) => component.kind),
    ...regions,
  ]).filter(Boolean);
  const searchText = joinSearchText([
    params.root.name ?? "",
    ...params.pages.map((page) => page.name ?? ""),
    ...semanticLabels,
    ...componentAnatomy.map((component) => component.kind),
    ...regions,
    ...interactionAffordances,
    layoutSignature.viewportClass,
    layoutSignature.primaryFlow,
    layoutSignature.density,
  ]);

  return {
    schemaVersion: "cogo.figma-template-search-representation.v1",
    key: `figma:${params.figmaFileId}:${params.figmaNodeId}`,
    source: {
      figmaFileId: params.figmaFileId,
      figmaNodeId: params.figmaNodeId,
      rootName: params.root.name,
      rootType: params.root.type,
    },
    searchText,
    aliases,
    semanticLabels,
    layoutSignature,
    componentAnatomy,
    designTokenSummary: {
      colorCount: exactTokenCounts.color,
      typographyCount: exactTokenCounts.typography,
      radiusCount: exactTokenCounts.radius,
      shadowCount: exactTokenCounts.shadow,
      gradientCount: exactTokenCounts.gradient,
    },
    compilerFacts: {
      sourceNodeIds: importedNodes
        .map((node) => node.id)
        .filter((id): id is string => Boolean(id)),
      pageBoxes,
      exactTokenCounts,
      assetFacts: {
        imageFillNodeIds,
        gradientFillNodeIds,
      },
    },
  };
}

export function buildFigmaDesignOntology(params: {
  figmaFileId: string;
  figmaNodeId: string;
  root: FigmaOntologyNode;
  pages: FigmaOntologyNode[];
  localImageAssetNodeIds?: string[];
  localSvgVectorNodeIds?: string[];
  sourceVectorUnavailableReason?: string;
}): FigmaDesignOntology {
  const allNodes = collectNodes(params.root);
  const importedNodes = params.pages.flatMap(collectNodes);
  const imageFillNodeIds = unique(
    importedNodes
      .filter(hasImageFill)
      .map((node) => node.id)
      .filter((id): id is string => Boolean(id)),
  );
  const localImageAssetNodeIds = unique(
    params.localImageAssetNodeIds ?? [],
  ).filter((id) => imageFillNodeIds.includes(id));
  const vectorLikeNodes = importedNodes.filter(isVectorLikeNode);
  const vectorLikeNodeIds = unique(
    vectorLikeNodes
      .map((node) => node.id)
      .filter((id): id is string => Boolean(id)),
  );
  const geometryNodeIds = unique(
    vectorLikeNodes
      .filter(hasVectorGeometry)
      .map((node) => node.id)
      .filter((id): id is string => Boolean(id)),
  );
  const localSvgNodeIds = unique(params.localSvgVectorNodeIds ?? []).filter(
    (id) => vectorLikeNodeIds.includes(id),
  );
  const exactVectorIds = new Set([...geometryNodeIds, ...localSvgNodeIds]);
  const primitiveFallbackNodeIds = vectorLikeNodeIds.filter(
    (id) => !exactVectorIds.has(id),
  );
  const vectorAreaRatio = areaRatioForNodes(vectorLikeNodes, params.pages);
  const imageCoverage = coverageRatio(
    imageFillNodeIds.length,
    localImageAssetNodeIds.length,
  );
  const vectorCoverage = coverageRatio(
    vectorLikeNodeIds.length,
    exactVectorIds.size,
  );
  const fallbackStrategy =
    vectorLikeNodeIds.length === 0 || primitiveFallbackNodeIds.length === 0
      ? "exact-vector"
      : vectorAreaRatio >= 0.08
        ? "source-raster-required"
        : "primitive-fallback";
  const reasons: string[] = [];
  if (imageCoverage < 1) {
    reasons.push(
      `Only ${localImageAssetNodeIds.length}/${imageFillNodeIds.length} image-fill nodes have persistent local assets.`,
    );
  }
  if (primitiveFallbackNodeIds.length > 0) {
    reasons.push(
      `${primitiveFallbackNodeIds.length}/${vectorLikeNodeIds.length} vector-like nodes require fallback; vectorAreaRatio=${vectorAreaRatio}.`,
    );
  }
  if (params.sourceVectorUnavailableReason) {
    reasons.push(
      `Source vector exactness is externally blocked: ${params.sourceVectorUnavailableReason}`,
    );
  }
  if (reasons.length === 0) {
    reasons.push(
      "Figma layout, style, image assets, and vector sources are enforced by the tool contract.",
    );
  }
  const status =
    imageCoverage < 1
      ? "fail"
      : primitiveFallbackNodeIds.length > 0 &&
          !params.sourceVectorUnavailableReason &&
          fallbackStrategy === "source-raster-required"
        ? "fail"
        : primitiveFallbackNodeIds.length > 0 ||
            params.sourceVectorUnavailableReason
          ? "warn"
          : "pass";

  return {
    schemaVersion: "cogo.figma-design-ontology.v1",
    source: {
      figmaFileId: params.figmaFileId,
      figmaNodeId: params.figmaNodeId,
      rootName: params.root.name,
      rootType: params.root.type,
    },
    authority: {
      layout: "figma.absoluteBoundingBox",
      style: "figma.fills/strokes/style/effects",
      assets: "figma.imageRef/rendered-assets",
      vectors: "figma.geometry.paths|figma.svg-render|cogo.primitive-fallback",
      llmMayOverride: false,
    },
    pages: params.pages.map((page, index) => {
      const pageNodes = collectNodes(page);
      const pageVectorNodes = pageNodes.filter(isVectorLikeNode);
      return {
        id: page.id ?? `page-${index + 1}`,
        name: page.name ?? `Page ${index + 1}`,
        routeHint: routeHintForPage(index, page.name),
        box: numericBox(page) ?? { x: 0, y: 0, width: 0, height: 0 },
        nodeCount: pageNodes.length,
        imageFillNodeCount: pageNodes.filter(hasImageFill).length,
        vectorLikeNodeCount: pageVectorNodes.length,
        vectorAreaRatio: areaRatioForNodes(pageVectorNodes, [page]),
      };
    }),
    tokens: {
      counts: tokenCounts(allNodes),
    },
    assets: {
      imageFillNodeIds,
      localImageAssetNodeIds,
      imageCoverage,
    },
    vectors: {
      vectorLikeNodeIds,
      geometryNodeIds,
      localSvgNodeIds,
      primitiveFallbackNodeIds,
      vectorCoverage,
      vectorAreaRatio,
      fallbackStrategy,
      ...(params.sourceVectorUnavailableReason
        ? { sourceUnavailableReason: params.sourceVectorUnavailableReason }
        : {}),
    },
    quality: {
      status,
      reasons,
    },
  };
}

export function scoreFigmaTemplateMatch(
  representation: FigmaTemplateSearchRepresentation,
  query: FigmaTemplateMatchQuery = {},
): FigmaTemplateMatchScore {
  const weights: FigmaTemplateMatchScore["weights"] = {
    semanticKindName: 0.3,
    anatomySlot: 0.2,
    designTokenFit: 0.2,
    layoutTopology: 0.15,
    interactionAffordance: 0.1,
    provenanceConfidence: 0.05,
  };
  const semanticIntentScore = scoreTextOverlap(
    query.userIntent,
    representation.searchText,
  );
  const anatomySlotScore = scoreSetOverlap(
    query.expectedComponentKinds,
    representation.layoutSignature.componentKinds,
  );
  const layoutTopologyScore =
    query.expectedViewportClass &&
    query.expectedViewportClass !== representation.layoutSignature.viewportClass
      ? 0.4
      : scoreSetOverlap(
          query.expectedRegions,
          representation.layoutSignature.regions,
        );
  const domainEntityScore = scoreTextOverlap(
    query.userIntent,
    representation.semanticLabels.join(" "),
  );
  const interactionAffordanceScore = scoreSetOverlap(
    query.expectedInteractionAffordances,
    representation.layoutSignature.interactionAffordances,
  );
  const designTokenCompatibility =
    representation.designTokenSummary.colorCount > 0 &&
    representation.designTokenSummary.typographyCount > 0
      ? 1
      : representation.designTokenSummary.colorCount > 0
        ? 0.7
        : 0.3;
  const provenanceScopeScore =
    query.scope === "globalLibrary"
      ? 0.55
      : query.scope === "designSystem"
        ? 0.75
        : 1;
  const semanticKindNameScore = Number(
    (semanticIntentScore * 0.67 + domainEntityScore * 0.33).toFixed(3),
  );
  const finalScore = Number(
    (
      semanticKindNameScore * weights.semanticKindName +
      anatomySlotScore * weights.anatomySlot +
      designTokenCompatibility * weights.designTokenFit +
      layoutTopologyScore * weights.layoutTopology +
      interactionAffordanceScore * weights.interactionAffordance +
      provenanceScopeScore * weights.provenanceConfidence
    ).toFixed(3),
  );

  return {
    schemaVersion: "cogo.figma-template-match-score.v1",
    finalScore,
    decision: decisionForScore(finalScore),
    components: {
      semanticIntentScore,
      anatomySlotScore,
      layoutTopologyScore,
      domainEntityScore,
      interactionAffordanceScore,
      designTokenCompatibility,
      provenanceScopeScore,
    },
    weights,
    thresholds: {
      matchClosedCore: 0.85,
      modifyExisting: 0.7,
      composeFromLowerLayer: 0.5,
    },
    notes: [
      "Late-fusion weights: semantic kind/name 0.30, anatomy/slot 0.20, design-token fit 0.20, layout topology 0.15, interaction affordance 0.10, provenance 0.05.",
      "Vector/search text excludes raw coordinates and raw design-token dumps.",
      "Exact layout/style/token facts stay in compilerFacts for injection and validation.",
      "LLM may use this score for explanation only; tool/validator owns acceptance.",
    ],
  };
}

export function chooseLayoutGenerationBoundary(input: {
  sourceLayoutAvailable: boolean;
  shouldReuseSourceLayout: boolean;
  layoutRecipeId?: string;
  responsive?: boolean;
}): CogoLayoutGenerationBoundary {
  if (input.sourceLayoutAvailable && input.shouldReuseSourceLayout) {
    return {
      schemaVersion: "cogo.layout-generation-boundary.v1",
      mode: "absolute-layout",
      authority: "figma.absoluteBoundingBox",
      engineRequired: false,
      llmMayEmitRawCoordinates: false,
      requiredInputs: [
        "figma.absoluteBoundingBox",
        "figma.fills/strokes/style/effects",
      ],
      reason: "Source/template layout is available and selected for reuse.",
    };
  }

  return {
    schemaVersion: "cogo.layout-generation-boundary.v1",
    mode: input.responsive ? "responsive-layout" : "constraint-layout",
    authority: input.responsive
      ? "ontology.responsiveLayoutRecipe"
      : "ontology.layoutRecipe",
    engineRequired: true,
    llmMayEmitRawCoordinates: false,
    requiredInputs: [
      input.layoutRecipeId
        ? `layoutRecipe:${input.layoutRecipeId}`
        : "layoutRecipe",
      "slotHierarchy",
      "spacingScale",
      "breakpointConstraints",
      "textFitRules",
      "tapTargetRules",
    ],
    reason:
      "No reusable source layout is authoritative; deterministic layout engine must calculate placement from ontology constraints.",
  };
}

export function buildFigmaTemplateEnforcementContract(params: {
  representation: FigmaTemplateSearchRepresentation;
  matchScore: FigmaTemplateMatchScore;
  layoutBoundary: CogoLayoutGenerationBoundary;
  query?: FigmaTemplateMatchQuery;
}): FigmaTemplateEnforcementContract {
  const generationPath = generationPathForDecision(params.matchScore.decision);
  const fallbackPolicy: FigmaTemplateEnforcementContract["fallbackPolicy"] = {
    pageMissing: "compose-page-from-ui-block-ontology",
    uiBlockMissing: "compose-ui-block-from-component-ontology",
    componentMissing: "create-component-candidate-and-queue-review",
    modificationDecision: modificationDecisionFor(params.matchScore.decision),
    promotionRequiresReview: true,
    validationRequired: true,
  };

  return {
    schemaVersion: "cogo.figma-template-enforcement-contract.v1",
    selectedTemplate: {
      key: params.representation.key,
      figmaFileId: params.representation.source.figmaFileId,
      figmaNodeId: params.representation.source.figmaNodeId,
      rootName: params.representation.source.rootName,
      rootType: params.representation.source.rootType,
      scope: params.query?.scope ?? "template",
      matchDecision: params.matchScore.decision,
      matchScore: params.matchScore.finalScore,
    },
    authority: {
      selection: "tool.match-score",
      layout: params.layoutBoundary.authority,
      style: "figma.fills/strokes/style/effects",
      tokens: "figma.fills/strokes/style/effects",
      assets: "figma.imageRef/rendered-assets",
      actionflow: "separate-actionflow-contract",
      llmMayChooseTemplate: false,
      llmMayOverrideLayout: false,
      llmMayOverrideStyle: false,
      llmMayPromoteFallback: false,
    },
    generationPath,
    requiredCompilerInputs: unique([
      ...params.layoutBoundary.requiredInputs,
      "compilerFacts.sourceNodeIds",
      "compilerFacts.pageBoxes",
      "compilerFacts.exactTokenCounts",
      "compilerFacts.assetFacts",
      "designOntology.assets",
      "designOntology.vectors",
    ]),
    lockedFacts: params.representation.compilerFacts,
    fallbackPolicy,
    validationGates: [
      "template-selection-tool-owned",
      "layout-boundary-llm-raw-coordinate-ban",
      "style-token-authority-lock",
      "figma-raster-lock-or-persistent-assets",
      "source-vs-generated-visual-diff",
      "commercial-visual-quality",
      "page-ui-block-component-fallback-review",
      "actionflow-contract-separate-before-publish",
    ],
    notes: [
      "The LLM may explain the selected template but must not choose a different template.",
      "Layout and style facts are injected after match from locked compiler facts.",
      "Missing page/UI-block/component ontology can create candidates only through the review queue.",
      "ActionFlow mutation and validation are intentionally delegated to the ActionFlow contract lane.",
    ],
  };
}
